import { computed, Injectable, signal } from '@angular/core';
import { enableMapSet, produce } from 'immer';

export type ExpandableItem = {
  id: string;
  expanded: boolean;
  parentId?: string;
  childrenIds?: string[];
  defaultExpanded?: boolean;
};

@Injectable()
export class ExpandService {
  private _expandedItems = signal<Map<string, ExpandableItem>>(new Map());

  public isAnyExpanded = computed(() => {
    for (const item of this._expandedItems().values()) {
      if (item.expanded) {
        return true;
      }
    }
    return false;
  });

  public getExpandState = (id: string) =>
    computed(() => {
      const item = this._expandedItems().get(id);
      return item ? item.expanded : false;
    });

  constructor() {
    enableMapSet();
  }

  public expandAll(): void {
    this._expandedItems.update((items) =>
      produce(items, (draft) => {
        draft.forEach((item, id) => {
          draft.set(id, { ...item, expanded: true });
        });
      }),
    );
  }

  public collapseAll(): void {
    this._expandedItems.update((items) =>
      produce(items, (draft) => {
        draft.forEach((item, id) => {
          draft.set(id, { ...item, expanded: false });
        });
      }),
    );
  }

  public reset(): void {
    this._expandedItems.update((items) =>
      produce(items, (draft) => {
        draft.forEach((item, id) => {
          draft.set(id, { ...item, expanded: item.defaultExpanded });
        });
      }),
    );
  }

  public addItem(id: string, expanded = true, parentId?: string): void {
    this._expandedItems.update((items) =>
      produce(items, (draft) => {
        if (draft.has(id)) {
          console.warn(`Item with id ${id} already exists.`);
          return;
        }

        const newItem: ExpandableItem = {
          id,
          expanded,
          parentId,
          childrenIds: [],
          defaultExpanded: expanded,
        };

        draft.set(id, newItem);

        if (parentId) {
          const parent = draft.get(parentId);
          if (parent) {
            draft.set(parentId, {
              ...parent,
              childrenIds: [...(parent.childrenIds ?? []), id],
            });
          }
        }
      }),
    );
  }

  public setExpandState(id: string, expanded: boolean): void {
    if (expanded) {
      this.expandItem(id);
    } else {
      this.collapseItem(id);
    }
  }

  private expandItem(id: string): void {
    this._expandedItems.update((items) =>
      produce(items, (draft) => {
        const item = draft.get(id);
        if (!item) {
          console.warn(`Item with id ${id} does not exist.`);
          return;
        }

        draft.set(id, { ...item, expanded: true });

        let parentId = item.parentId;
        while (parentId) {
          const parent = draft.get(parentId);
          if (parent) {
            draft.set(parentId, { ...parent, expanded: true });
            parentId = parent.parentId;
          } else {
            parentId = undefined;
          }
        }
      }),
    );
  }

  private collapseItem(id: string): void {
    this._expandedItems.update((items) =>
      produce(items, (draft) => {
        const collapseRecursively = (nodeId: string): void => {
          const node = draft.get(nodeId);
          if (!node) return;

          draft.set(nodeId, { ...node, expanded: false });

          for (const childId of node.childrenIds ?? []) {
            collapseRecursively(childId);
          }
        };

        if (!draft.has(id)) {
          console.warn(`Item with id ${id} does not exist.`);
          return;
        }

        collapseRecursively(id);
      }),
    );
  }
}
